import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Compass,
  LayoutDashboard,
  Route as RouteIcon,
  GitBranch,
  Sparkles,
  BookOpen,
  Building2,
  Timer,
  DollarSign,
  Battery,
  Eye,
} from "lucide-react";

const beliefs = [
  { letter: "T", icon: Timer, title: "Time", body: "Weeks preparing for the wrong exam. Hours of counselor sessions repeating what should have been settled up front." },
  { letter: "E", icon: Battery, title: "Energy", body: "Faculty and students carrying decision loads that a single instrument could have carried instead." },
  { letter: "A", icon: Eye, title: "Attention", body: "The scarcest resource in any school. Spent on defaults nobody chose — instead of the students and decisions in front of you." },
  { letter: "M", icon: DollarSign, title: "Money", body: "Test prep, technology and programs bought against a hypothesis rather than a diagnostic. Every semester it compounds." },
];

const tools = [
  {
    icon: LayoutDashboard,
    tag: "Decision-Intelligence Platform",
    name: "The Helm™",
    body:
      "One console for school leaders to see every student's decision profile in one place, track progress and act on it. Admin dashboard, individual student profiles, and a school membership portal that turns a one-time read into an ongoing capability.",
    detail: "Admin console · Student profiles · Membership portal",
    included: "Included with School Membership & Enterprise",
    to: "/testfit",
    cta: "See how it connects to TestFit®",
  },
  {
    icon: GitBranch,
    tag: "Decision Frameworks",
    name: "The Big Decision Blueprint™",
    body:
      "A repeatable framework that converts high-stakes choices — leadership transitions, program pivots, resource allocation, family-facing recommendations — into a defensible method. Used by leadership teams to make change decisions with confidence.",
    detail: "Facilitated with your team · Self-guided for individuals",
    included: "Contact for pricing",
    to: "/leadership",
    cta: "Explore leadership use",
  },
  {
    icon: Compass,
    tag: "Leadership Diagnostic",
    name: "Genius Leadership Archetype™ Assessment",
    body:
      "Show leaders how they actually decide — their natural pattern, their secondary style, their blind spots — before the next high-stakes moment. Delivered as an instrument for individuals or as team mapping for full leadership groups.",
    detail: "Individual results · Team archetype mapping",
    included: "Contact for pricing",
    to: "/gla",
    cta: "See how the Genius Leader Archetype™ & GLAD Score™ work",
  },
  {
    icon: RouteIcon,
    tag: "Done-For-You Engagement",
    name: "The Living Course Method™",
    body:
      "We turn an existing course, program or workshop into a decision-driven experience — an instrument students finish and act on, not a container they consume and forget. Scoped to your curriculum in two engagement tiers.",
    detail: "Two tiers · Scoped to your existing content",
    included: "$15K · $25K",
    to: "/contact?interest=custom_system",
    cta: "Discuss your course",
  },
];

const flow = [
  { step: "01", title: "Diagnose", body: "Read the student, the leader, or the institutional moment with an instrument built for the question — not a survey." },
  { step: "02", title: "Decide", body: "Convert the diagnostic into the most strategic next step — with a defensible frame that survives board and parent review." },
  { step: "03", title: "Design", body: "Shape the program, plan or protocol around the actual read — not around a template." },
  { step: "04", title: "Deliver", body: "Put the decision into practice. Measure it. Come back and read again." },
];

export default function Curriculum() {
  return (
    <div data-testid="page-curriculum">
      {/* HERO */}
      <section className="relative bg-[#F0EDE6]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 pt-28 pb-24 md:pt-36 md:pb-32">
          <div className="overline text-[#0F4C5C]">Decision Tools &amp; Frameworks</div>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.05] mt-4 max-w-4xl">
            The decision <em className="italic text-[#0F4C5C]">comes first.</em>
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#22223B]/80 leading-relaxed max-w-3xl">
            The thing we do best is build instruments — small, precise tools that reveal what a student, a leader or a team actually needs, so schools can stop guessing and start deciding. Clarity comes first. Content, curriculum and programs follow. The instrument is the point.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact?interest=custom_system" data-testid="dt-cta-primary" className="btn-primary">
              Discuss a Custom Tool <ArrowUpRight size={16} />
            </Link>
            <Link to="/contact?interest=curriculum" className="btn-secondary">
              Explore Existing Instruments
            </Link>
          </div>
        </div>
      </section>

      {/* THE BELIEF */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-7">
              <div className="overline">The belief — T.E.A.M.</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                Every school is quietly misusing its <em className="italic text-[#0F4C5C]">TEAM.</em>
              </h2>
            </div>
            <p className="md:col-span-5 text-[#22223B]/75 leading-relaxed">
              Time, energy, attention and money — the four resources every school spends every day. When decisions run on defaults, all four are misused, and staff and student wellbeing pays the tax.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {beliefs.map((b) => (
              <div key={b.title} className="card-soft p-8 relative">
                <div className="absolute top-6 right-6 font-serif-display italic text-6xl text-[#E3B23C]/25 leading-none select-none">
                  {b.letter}
                </div>
                <b.icon size={26} strokeWidth={1.4} className="text-[#E3B23C]" />
                <h3 className="font-serif-display text-xl mt-5">{b.title}</h3>
                <p className="mt-3 text-[15px] text-[#22223B]/75 leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE TOOL STACK */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-8">
              <div className="overline">The instrument stack</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                One console. Four tools. <em className="italic text-[#0F4C5C]">One clear picture.</em>
              </h2>
            </div>
            <p className="md:col-span-4 text-[#22223B]/75 leading-relaxed">
              Each instrument answers a different question — student, leader, team, program. Together they form a coherent decision-intelligence layer for your school.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {tools.map((t) => (
              <div key={t.name} className="card-soft p-8 md:p-10 flex flex-col">
                <div className="flex items-start justify-between gap-6">
                  <t.icon size={28} strokeWidth={1.4} className="text-[#0F4C5C]" />
                  <span className="text-xs uppercase tracking-widest text-[#0F4C5C] font-semibold text-right">
                    {t.tag}
                  </span>
                </div>
                <h3 className="font-serif-display text-2xl md:text-3xl mt-6 leading-snug">{t.name}</h3>
                <p className="mt-4 text-[15px] text-[#22223B]/80 leading-relaxed flex-1">{t.body}</p>
                <div className="mt-6 pt-6 border-t border-black/5 flex items-center justify-between text-sm">
                  <span className="text-[#6B705C]">{t.detail}</span>
                  <span className="font-serif-display text-[#0F4C5C] italic">{t.included}</span>
                </div>
                <Link to={t.to} className="link-underline mt-4 inline-flex items-center gap-1 self-start text-sm">
                  {t.cta} <ArrowUpRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="section-pad bg-[#0F4C5C] text-[#FDFBF7] grain relative overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <div className="overline text-[#E3B23C]">The method</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                Diagnose. Decide. <em className="italic text-[#E3B23C]">Design.</em> Deliver.
              </h2>
            </div>
            <p className="md:col-span-5 text-[#FDFBF7]/80 leading-relaxed">
              Every EdgeTech instrument runs the same operating loop. It's defensible for boards, useful for counselors, and clear enough for a 16-year-old to trust.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {flow.map((f) => (
              <div key={f.title} className="p-8 border border-white/10 rounded-lg bg-white/[0.03]">
                <div className="overline text-[#FDFBF7]/50">{f.step}</div>
                <h3 className="font-serif-display text-2xl mt-4">{f.title}</h3>
                <p className="mt-3 text-sm text-[#FDFBF7]/75 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT SAVES TIME MONEY ENERGY */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <div className="overline">The economics</div>
            <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
              Instruments make schools <em className="italic text-[#0F4C5C]">cheaper to run.</em>
            </h2>
            <p className="mt-6 text-[17px] text-[#22223B]/80 leading-relaxed">
              A five-minute read at the start of a decision is worth more than a five-hour meeting three months in. Every EdgeTech tool is built so a single decision-maker can approve it — under the cost of a textbook, above the utility of a program — with a return your board can defend.
            </p>
          </div>
          <div className="md:col-span-6 grid grid-cols-2 gap-4">
            {[
              { k: "Under 20 min", v: "average student read on TestFit®" },
              { k: "One approver", v: "priced under committee threshold" },
              { k: "$3,500 / yr", v: "school-wide membership entry point" },
              { k: "50 → ∞", v: "licenses scale with your enrollment" },
            ].map((s) => (
              <div key={s.k} className="p-6 rounded-lg border border-black/5 bg-[#F0EDE6]">
                <div className="font-serif-display text-3xl text-[#0F4C5C]">{s.k}</div>
                <div className="text-sm text-[#22223B]/70 mt-2">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CURRICULUM AS BYPRODUCT */}
      <section className="section-pad bg-[#F0EDE6]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-5">
              <div className="overline">And yes — curriculum, too</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight">
                Content that came from <em className="italic text-[#0F4C5C]">clarity.</em>
              </h2>
            </div>
            <div className="md:col-span-7 text-[#22223B]/80 leading-relaxed space-y-4">
              <p>
                Over the years the instruments have produced curriculum as a natural byproduct — once you know what a student needs, the lessons practically write themselves. College &amp; career readiness. Test-taking strategy. Financial literacy. Personal responsibility. Leadership. All built as decision-driven, not content-driven.
              </p>
              <p className="text-[#22223B]/70">
                We still ship these when they're the right fit for a school. But we lead with the instrument, because that's where the value actually is.
              </p>
              <div className="pt-4 grid grid-cols-2 gap-3 text-sm text-[#22223B]/75">
                {[
                  "College & Career Readiness",
                  "Test-Taking Strategy",
                  "Financial Literacy",
                  "Leadership Curriculum",
                  "Personal Responsibility",
                  "AI-Supported Learning",
                ].map((c) => (
                  <div key={c} className="flex gap-2 items-center">
                    <BookOpen size={14} className="text-[#6B705C]" strokeWidth={1.6} /> {c}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="section-pad bg-[#FDFBF7]">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <div className="overline">Custom instrument?</div>
          <h2 className="font-serif-display text-4xl md:text-6xl mt-4 leading-tight">
            Bring us the decision. <em className="italic text-[#0F4C5C]">We'll build the instrument.</em>
          </h2>
          <p className="mt-6 text-[#22223B]/75 max-w-xl mx-auto">
            If your school is making the same complex decision over and over — admissions fit, placement, program selection, teacher assignment, leadership succession — there's an instrument for that. We build it.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact?interest=custom_system" data-testid="dt-bottom-cta" className="btn-primary">
              Design a Custom Tool <ArrowUpRight size={16} />
            </Link>
            <Link to="/testfit" className="btn-secondary">
              Start with TestFit®
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
